import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { safeStorage } from '../utils/safeStorage';

interface PreferencesContextType {
  // Theme
  theme: 'light' | 'dark' | 'system';
  setTheme: (theme: 'light' | 'dark' | 'system') => void;

  // Display preferences
  showCurrencySymbols: boolean;
  setShowCurrencySymbols: (value: boolean) => void;

  use12HourFormat: boolean;
  setUse12HourFormat: (value: boolean) => void;

  compactTableView: boolean;
  setCompactTableView: (value: boolean) => void;

  timezone: string;
  setTimezone: (value: string) => void;
}

const PreferencesContext = createContext<PreferencesContextType | undefined>(undefined);

interface PreferencesProviderProps {
  children: ReactNode;
}

export const PreferencesProvider: React.FC<PreferencesProviderProps> = ({ children }) => {
  // Load from localStorage with defaults
  const [theme, setThemeState] = useState<'light' | 'dark' | 'system'>(() => {
    const saved = safeStorage.getItem('theme') as 'light' | 'dark' | 'system' | null;
    return saved || 'light';
  });

  const [showCurrencySymbols, setShowCurrencySymbolsState] = useState(() => {
    const saved = safeStorage.getItem('showCurrencySymbols');
    return saved ? JSON.parse(saved) : true;
  });

  const [use12HourFormat, setUse12HourFormatState] = useState(() => {
    const saved = safeStorage.getItem('use12HourFormat');
    return saved ? JSON.parse(saved) : true;
  });

  const [compactTableView, setCompactTableViewState] = useState(() => {
    const saved = safeStorage.getItem('compactTableView');
    return saved ? JSON.parse(saved) : false;
  });

  const [timezone, setTimezoneState] = useState(() => {
    const saved = safeStorage.getItem('timezone');
    return saved || 'America/New_York';
  });

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;

    if (theme === 'dark') {
      root.classList.add('dark');
    } else if (theme === 'light') {
      root.classList.remove('dark');
    } else if (theme === 'system') {
      // Check system preference
      const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (systemPrefersDark) {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
    }

    // Save to localStorage
    safeStorage.setItem('theme', theme);
  }, [theme]);

  // Wrapper functions that save to localStorage
  const setTheme = (value: 'light' | 'dark' | 'system') => {
    setThemeState(value);
  };

  const setShowCurrencySymbols = (value: boolean) => {
    setShowCurrencySymbolsState(value);
    safeStorage.setItem('showCurrencySymbols', JSON.stringify(value));
  };

  const setUse12HourFormat = (value: boolean) => {
    setUse12HourFormatState(value);
    safeStorage.setItem('use12HourFormat', JSON.stringify(value));
  };

  const setCompactTableView = (value: boolean) => {
    setCompactTableViewState(value);
    safeStorage.setItem('compactTableView', JSON.stringify(value));
  };

  const setTimezone = (value: string) => {
    setTimezoneState(value);
    safeStorage.setItem('timezone', value);
  };

  const value: PreferencesContextType = {
    theme,
    setTheme,
    showCurrencySymbols,
    setShowCurrencySymbols,
    use12HourFormat,
    setUse12HourFormat,
    compactTableView,
    setCompactTableView,
    timezone,
    setTimezone,
  };

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
};

export const usePreferences = (): PreferencesContextType => {
  const context = useContext(PreferencesContext);
  if (context === undefined) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
};
